// Offert / kalkyl builder — with AI draft from historical data
const OffertScreen = () => {
  const [aiState, setAiState] = React.useState("ready"); // ready | thinking | done
  const [prompt, setPrompt] = React.useState("");
  const FULL_PROMPT = "Köksrenov HTH, Siemens iQ500, Lidingö, ~6 mansveckor";

  // On mount: type prompt → thinking → done (no loop)
  React.useEffect(() => {
    let timers = [];
    let i = 0;
    const typeStep = () => {
      i++;
      setPrompt(FULL_PROMPT.slice(0, i));
      if (i < FULL_PROMPT.length) {
        timers.push(setTimeout(typeStep, 32 + Math.random() * 30));
      } else {
        timers.push(setTimeout(() => setAiState("thinking"), 500));
        timers.push(setTimeout(() => setAiState("done"), 2400));
      }
    };
    timers.push(setTimeout(typeStep, 600));
    return () => timers.forEach(clearTimeout);
  }, []);

  const rows = [
    { kind:"section", label:"Arbete" },
    { name:"Snickeri & montage", qty:120, unit:"tim", material:0, labour:72000, total:72000, ai:true },
    { name:"Vvs — diskbänk, maskiner", qty:24, unit:"tim", material:1800, labour:20400, total:22200, ai:true },
    { name:"El — induktionshäll, belysning", qty:16, unit:"tim", material:2400, labour:13600, total:16000, ai:true },
    { kind:"section", label:"Material" },
    { name:"Material enligt specifikation", qty:1, unit:"post", material:85300, labour:0, total:85300, ai:true, note:"Luckor, bänkskiva, vitvaror, beslag" },
    { kind:"section", label:"Framkörning" },
    { name:"Framkörning & etablering", qty:1, unit:"st", material:0, labour:2400, total:2400, ai:true },
  ];

  return (
    <div className="screen-frame" data-label="Offert / kalkyl">
      <ScreenChrome title="Offert · Familjen Lindqvist" />
      <div className="screen-body offert-body">
        <div className="offert-meta">
          <div>
            <div className="offert-title">Köksrenovering — Lidingö</div>
            <div style={{fontSize:13, color:'var(--ink-3)', marginTop:4}}>Offert #2026-0148 · Skapad 22 apr · Giltig t.o.m 22 maj</div>
          </div>
          <div style={{display:'flex', gap:8}}>
            <span className="seg-btn">Förhandsgranska</span>
            <span className="seg-btn primary">Skicka för signering</span>
          </div>
        </div>

        {/* AI draft band */}
        <div className={`ai-band ${aiState}`}>
          <div className="ai-band-row">
            <div className="ai-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1.5l1.6 4.4 4.4 1.6-4.4 1.6L8 13.5 6.4 9.1 2 7.5l4.4-1.6L8 1.5z" fill="currentColor"/>
                <circle cx="13" cy="3" r="1.2" fill="currentColor"/>
              </svg>
            </div>
            <div className={`ai-input-wrap ${aiState === 'ready' && prompt.length > 0 && prompt.length < FULL_PROMPT.length ? 'typing' : ''}`}>
              <input
                className="ai-input"
                value={prompt}
                readOnly
                placeholder="Beskriv jobbet i en mening — Byggro hittar liknande projekt och föreslår rader…"
              />
              {aiState === "ready" && prompt.length < FULL_PROMPT.length && <span className="ai-caret"/>}
              <span className="ai-hint mono">↩ för utkast</span>
            </div>
            <button className={`ai-btn ${aiState === "thinking" ? "loading" : ""}`}>
              {aiState === "thinking" ? "Läser historik…" : aiState === "done" ? "Utkast klart ✓" : "Föreslå utkast"}
            </button>
          </div>

          {aiState === "thinking" && (
            <div className="ai-progress">
              <div className="ai-progress-bar"/>
              <div className="ai-progress-steps mono">
                <span className="active">Matchar 247 tidigare offerter</span>
                <span>·</span>
                <span className="active">Hämtar prislistor (Beijer, Optimera)</span>
                <span>·</span>
                <span>Räknar marginal mot utfall</span>
              </div>
            </div>
          )}

          {aiState === "done" && (
            <div className="ai-result">
              <div className="ai-sources">
                <div className="ai-sources-label mono">Baserat på 4 liknande projekt</div>
                <div className="ai-sources-list">
                  <SourcePill name="Familjen Bergström" type="Kök · Bromma · 2025" match={94} />
                  <SourcePill name="Andersson, kvarteret Tor" type="Kök · Lidingö · 2025" match={89} />
                  <SourcePill name="BRF Klövern lgh 14" type="Kök · Sthlm · 2024" match={82} />
                  <SourcePill name="Villa Ekgården" type="Kök · Nacka · 2024" match={76} />
                </div>
              </div>
              <div className="ai-summary mono">
                <span style={{color:'var(--accent-green)'}}>✓</span> 9 rader infogade · 160 arbetstimmar · prognostiserad TB 31 % · slutkalkyl-snitt på liknande jobb: <b>+2.4 % över offert</b>
              </div>
            </div>
          )}
        </div>

        <div className="offert-grid">
          <div className="offert-table card">
            <div className="offert-thead">
              <div>Post</div>
              <div className="ta-r">Antal</div>
              <div className="ta-r">Material</div>
              <div className="ta-r">Arbete</div>
              <div className="ta-r">Summa</div>
            </div>
            {rows.map((r,i) => {
              if (aiState !== 'done') return null;
              return r.kind === 'section' ? (
                <div key={i} className="offert-section">{r.label}</div>
              ) : (
                <div key={i} className={`offert-row ${r.ai ? 'ai-row' : ''}`}>
                  <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
                    {r.ai && <span className="ai-dot" title="Föreslagen av Byggro AI"/>}
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13.5}}>{r.name}</div>
                      <div style={{fontSize:11.5, color:'var(--ink-4)', marginTop:2}}>{r.note ? r.note : `${r.qty} ${r.unit}`}</div>
                    </div>
                  </div>
                  <div className="ta-r mono">{r.qty}</div>
                  <div className="ta-r mono">{r.material ? r.material.toLocaleString('sv-SE') : '—'}</div>
                  <div className="ta-r mono">{r.labour ? r.labour.toLocaleString('sv-SE') : '—'}</div>
                  <div className="ta-r mono" style={{fontWeight:600}}>{r.total.toLocaleString('sv-SE')}</div>
                </div>
              );
            })}
            {aiState !== 'done' && (
              <div className="offert-empty">
                <div className="offert-empty-illu">
                  <div className="offert-empty-line" style={{width:'70%'}}/>
                  <div className="offert-empty-line" style={{width:'45%'}}/>
                  <div className="offert-empty-line" style={{width:'58%'}}/>
                  <div className="offert-empty-line" style={{width:'40%'}}/>
                </div>
                <div className="offert-empty-title">Ingen offert än</div>
                <div className="offert-empty-sub">Beskriv jobbet i raden ovan så föreslår Byggro rader från liknande projekt — eller börja från tom mall.</div>
                <div className="offert-empty-actions">
                  <button className="seg-btn primary">+ Lägg till rad manuellt</button>
                  <button className="seg-btn">Använd mall</button>
                  <button className="seg-btn">Importera från tidigare offert</button>
                </div>
              </div>
            )}
            {aiState === 'done' && <div className="offert-add">+ Lägg till rad eller mall…</div>}
          </div>
          <div className="offert-summary card">
            <div className="summary-section">
              <div className="summary-head">Sammanfattning</div>
              <Row k={aiState === 'done' ? "Arbete (160 tim)" : "Arbete"} v={aiState === 'done' ? "106 000" : "—"} />
              <Row k="Material" v={aiState === 'done' ? "85 300" : "—"} />
              <Row k="Påslag (12 %)" v={aiState === 'done' ? "22 956" : "—"} />
              <Row k="ROT-avdrag" v={aiState === 'done' ? "−31 800" : "—"} muted />
              <div className="summary-divider"/>
              <Row k="Offert" v={aiState === 'done' ? "182 456" : "—"} big />
              <Row k="varav moms" v={aiState === 'done' ? "36 491" : "—"} muted small />
            </div>
            <div className="summary-section">
              <div className="summary-head">Marginal</div>
              <div className="margin-vis">
                <div className="margin-bar"><div className="margin-fill" style={{width: aiState === 'done' ? '31%' : '0%'}}/></div>
                <div style={{display:'flex', justifyContent:'space-between', fontSize:12, marginTop:8, color:'var(--ink-3)'}}>
                  <span>{aiState === 'done' ? 'TB 31 % · 39 290 kr' : 'Väntar på utkast…'}</span>
                  {aiState === 'done' && <span className="mono" style={{color:'var(--accent-green)'}}>+4 % vs mall</span>}
                </div>
              </div>
            </div>
            <div className="summary-section">
              <div className="summary-head">Tidplan</div>
              <Row k="Beräknad start" v={aiState === 'done' ? "12 maj" : "—"} />
              <Row k="Avslut" v={aiState === 'done' ? "18 jun" : "—"} />
              <Row k="Arbetsdagar" v={aiState === 'done' ? "27" : "—"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SourcePill = ({name, type, match}) => (
  <div className="source-pill">
    <div>
      <div style={{fontSize:12, fontWeight:500}}>{name}</div>
      <div style={{fontSize:10.5, color:'var(--ink-4)', marginTop:1}}>{type}</div>
    </div>
    <span className="match mono">{match}%</span>
  </div>
);

const Row = ({k, v, muted, big, small}) => (
  <div style={{
    display:'flex',
    justifyContent:'space-between',
    padding: big ? '10px 0' : '6px 0',
    fontSize: small ? 12 : 13.5,
    color: muted ? 'var(--ink-3)' : 'var(--ink-2)',
    fontWeight: big ? 600 : 400,
  }}>
    <span>{k}</span>
    <span className="mono" style={{color: big ? 'var(--ink-1)' : 'inherit', fontSize: big ? 18 : 'inherit'}}>{v} kr</span>
  </div>
);

Object.assign(window, { OffertScreen });
